import { getRouteApi, Link } from '@tanstack/react-router';
import { Button } from '@tempo-io/sui-button';
import { ErrorIndicator, SquareAttention } from '@tempo-io/sui-icons';
import { toast } from '@tempo-io/sui-sonner';
import { Spinner } from '@tempo-io/sui-spinner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { useTranslation } from 'react-i18next';
import { ItemSyncStatusView } from '@/lib/api/types/work-integrations';
import { useIsIntegrationAllowedForItemLevel } from '@/lib/hooks/work-integrations/useIsIntegrationAllowedForItemType';
import { useRequestItemSyncMutation } from '@/lib/mutations/work-integrations/useRequestItemSyncMutation';
import { useIsItemLevelMapped } from '@/lib/queries/work-integrations/useIsItemLevelMapped';
import type { DataTableItem } from '../types';

export const IntegrationCell = ({
  itemId,
  integration,
  itemLevelId,
  syncStatus,
  canEditIntegrations,
}: {
  itemId: number;
  integration?: DataTableItem['integration'];
  itemLevelId?: number;
  syncStatus?: DataTableItem['syncStatus'];
  canEditIntegrations: boolean;
}) => {
  const { t } = useTranslation();
  const orgRoute = getRouteApi('/(app)/org/$org');
  const { org } = orgRoute.useParams();
  const requestSync = useRequestItemSyncMutation();
  const isIntegrationAllowed = useIsIntegrationAllowedForItemLevel(itemLevelId);

  const { isMapped, isLoading } = useIsItemLevelMapped({
    instanceId: integration?.instanceId,
    itemLevelId,
  });

  // Level 1/2 items can't be pushed (NEX-14309). Preserve display for items already synced
  // before this rule landed; for everything else (pending, failed, in-progress, unassigned)
  // render "-" with no controls.
  if (!isIntegrationAllowed && !integration) {
    return <span>-</span>;
  }

  if (syncStatus?.state === ItemSyncStatusView.FAILED) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="inline-flex items-center gap-1 text-text-danger"
              data-testid="item-sync-status-failed"
            >
              <ErrorIndicator className="h-4 w-4 shrink-0" />
              {t(($) => $.itemsTable.integrationCell.failed)}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-60">
            {syncStatus.errorMessage ?? t(($) => $.itemsTable.integrationCell.failedFallback)}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (integration) {
    const label = integration.name || integration.instanceUrl || integration.id;
    const showUnmappedWarning = !isLoading && !isMapped;

    return (
      <span className="flex items-center gap-1">
        {canEditIntegrations ? (
          <Button
            variant="link"
            asChild
            disabled={!org}
            className="text-text-link hover:text-text-info-bold disabled:cursor-not-allowed disabled:opacity-60 h-auto font-normal p-0"
          >
            <Link to="/org/$org/settings/work-integrations" params={{ org }}>
              {label}
            </Link>
          </Button>
        ) : (
          <span>{label}</span>
        )}
        {showUnmappedWarning && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <SquareAttention
                  className="text-text-warning"
                  data-testid="unmapped-level-warning-icon"
                />
              </TooltipTrigger>
              <TooltipContent className="max-w-60">
                {t(($) => $.itemsTable.integrationCell.unmappedLevel.tooltip)}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </span>
    );
  }

  const state = syncStatus?.state;

  if (state === ItemSyncStatusView.IN_PROGRESS || requestSync.isPending) {
    return (
      <span className="inline-flex items-center gap-2 text-text-subtle">
        <Spinner size={14} />
        {t(($) => $.itemsTable.integrationCell.syncing)}
      </span>
    );
  }

  if (state === ItemSyncStatusView.PENDING && canEditIntegrations) {
    return (
      <Button
        variant="link"
        size="sm"
        disabled={requestSync.isPending}
        className="h-auto p-0 font-normal"
        onClick={() =>
          requestSync.mutate(
            { itemId },
            {
              onError: () => toast.error(t(($) => $.itemsTable.integrationCell.syncToJiraError)),
            },
          )
        }
      >
        {t(($) => $.itemsTable.integrationCell.syncToJira)}
      </Button>
    );
  }

  return <span>-</span>;
};
